import React, { forwardRef, useRef, useCallback, useState } from "react";
import { Dialog, DialogContent, Grid } from "@mui/material";
import clsx from "clsx";
import GradientColorHeader from "@icoach/components/dialog/GradientColorHeader";
import { Alert, Box, Button, Card, DateField, Divider, Form, Skeleton, Stack, TextField, Typography } from "@curves/ui-base";
import { CyclePurchaseSummaryCard, CartTable } from "@icoach/healthyCyclePurchase/components";
import OrderInvoiceSection from "@icoach/accountBook/createOrder/components/OrderInvoiceSection";
import { useCyclePurchaseFirstOrder, useCyclePurchaseFirstOrderSubmit, useHCProductData } from "@icoach/healthyCyclePurchase/hooks";
import { formatCurrencyFn, refIsRequiredError, isFunction, localClub, getErrorModuleStatus } from "@util/utils";
import { getFirstOfMonth, getNowTime } from "@util/moment";
import { EmployeeSelect } from "@icoach/components";
import { useSnackbar } from "notistack";
import usePriceSummary from "@icoach/accountBook/createOrder/hooks/usePriceSummary";

const HealthyCycleFirstOrderDialog = forwardRef(({ open, sourceData, refresh, onClose }, _ref) => {
	if (!open) return null;

	const { cycleOrderID } = sourceData || {};
	const { enqueueSnackbar } = useSnackbar();

	// 使用首購 Hook 取得首購資料
	const { initialItems, invoiceSetting, cycleOrderInfo, member, options } = useCyclePurchaseFirstOrder({
		cycleOrderID,
		isFetchOnMount: true,
	});

	// 候選商品（給 CartTable 換口味／新增使用）
	const products = useHCProductData();

	// 購物車總額（由 CartTable onUpdateProduct 回拋）
	const [orderTotal, setOrderTotal] = useState(0);

	// 使用提交 Hook
	const { submit, isSubmitting, validationErrors, clearValidationErrors } = useCyclePurchaseFirstOrderSubmit({
		onSuccess: () => {
			isFunction(onClose) && onClose();
			isFunction(refresh) && refresh();
		},
	});

	// 表單 refs



	const productItemsRef = useRef(null);
	const invoiceSectionRef = useRef(null);
	const employeeRef = useRef(null);
	const orderDateRef = useRef(null);
	const cashPaymentRef = useRef(null);
	const cardPaymentRef = useRef(null);

	// CartTable 內容變動時更新總額
	const handleUpdateProduct = useCallback(items => {
		const total = (items || []).reduce((sum, item) => sum + (item.total || 0), 0);
		setOrderTotal(total);
	}, []);

	// 使用價格摘要 Hook
	const { cashPayment, cardPayment, paidAmount, balanceStatus, onCashPaymentChange, onCardPaymentChange, syncFromRefs, getPaymentResults, getPaymentMatchStatus } = usePriceSummary({
		orderTotal,
		refs: {
			cashPaymentRef,
			cardPaymentRef,
		},
	});

	// 取得購物車品項（已過濾掉數量 < 1 的）
	const getCartProducts = useCallback(() => {
		const items = productItemsRef.current?.getResult?.() || [];
		return items
			.filter(item => Number(item.quantity) > 0)
			.map(item => ({
				assignPrice: item.salePrice,
				quantity: item.quantity,
				notes: "",
				productID: item.productID,
				variantID: item.variantID,
			}));
	}, []);

	// 組裝 API 參數
	const getParams = useCallback(() => {
		const clubData = localClub.get();
		const params = {
			cycleOrderID: cycleOrderID || 0,
			clubID: clubData?.value || 0,
			receiverName: member?.memberName,
			receiverPhone: member?.cellPhone,
			purchaseTimeType: 0, // 預設值
			purchaseToolType: 0, // 預設值
			products: getCartProducts(),
		};

		// 取得發票設定
		if (invoiceSectionRef.current?.getResult) {
			params.invoiceSetting = invoiceSectionRef.current.getResult();
		}

		// 取得擔當員工ID
		if (employeeRef.current?.getResult) {
			params.employeeID = employeeRef.current.getResult() || 0;
		}

		// 取得訂單建立日期
		if (orderDateRef.current?.getResult) {
			params.orderDate = orderDateRef.current.getResult();
		}

		// 取得付款資訊
		const { cashPayment, cardPayment } = getPaymentResults();
		params.cashPayment = cashPayment;
		params.cardPayment = cardPayment;

		return params;
		// eslint-disable-next-line
	}, [cycleOrderID, member, getCartProducts, getPaymentResults]);

	// 檢查表單錯誤
	const checkFormErrors = useCallback(() => {
		// 先同步輸入框的值到 state
		syncFromRefs();

		const isError = refIsRequiredError(invoiceSectionRef, employeeRef, cashPaymentRef, cardPaymentRef);
		if (isError) return true;

		// 檢查購物車是否為空（至少 1 項）
		if (getCartProducts().length < 1 || orderTotal <= 0) {
			enqueueSnackbar("請至少選購一項商品，才能建立訂單。", { variant: "error" });
			return true;
		}

		// 檢查價格摘要的付款金額是否正確
		const paymentValidation = getPaymentMatchStatus();
		if (!paymentValidation.isValid) {
			enqueueSnackbar(paymentValidation.message, { variant: "error" });
			return true;
		}

		return false;
		// eslint-disable-next-line
	}, [getCartProducts, orderTotal, enqueueSnackbar, getPaymentMatchStatus]);

	// 送出結帳
	const handleSubmit = useCallback(() => {
		// 防止重複提交
		if (isSubmitting()) return;

		// 清除舊的錯誤訊息
		clearValidationErrors();

		// 檢查表單錯誤
		if (checkFormErrors()) return;

		// 組裝參數並提交
		const params = getParams();
		submit(params);

		// eslint-disable-next-line
	}, [checkFormErrors, getParams, submit]);

	return (
		<Dialog className={clsx("simple-content", "dialog")} PaperProps={{ className: "wx-120rem", component: Form }} open={open} fullWidth onSubmit={handleSubmit}>
			<GradientColorHeader onClose={onClose}>循環訂單現場首購</GradientColorHeader>
			<DialogContent className={"bg-default"}>
				{/* HC 主單摘要 */}
				<CyclePurchaseSummaryCard
					sourceData={{
						memberName: member?.memberName,
						memberNo: member?.memberNo,
						cellPhone: member?.cellPhone,
						cycleOrderTypeText: cycleOrderInfo?.cycleOrderTypeText,
						cycleOrderNo: cycleOrderInfo?.cycleOrderNo,
						saleAmount: cycleOrderInfo?.saleAmount,
						shippingDayText: cycleOrderInfo?.shippingDayText,
						firstShipmentMonth: cycleOrderInfo?.firstShipmentMonth,
						cycleOrderDate: cycleOrderInfo?.cycleOrderDate,
					}}
				/>
				{/* 購物車（可換口味／調整數量／新增／刪除） */}
				<Card className="mb-2">
					<Card.CardContent>
						{initialItems != null ? (
							<CartTable ref={productItemsRef} sourceData={initialItems} resource={products} onUpdateProduct={handleUpdateProduct} footerNote={null} showResetButton />
						) : (
							<Stack direction="column" spacing={1} className="p-2">
								<Skeleton animation="wave" variant="rectangular" height={48} />
								<Skeleton animation="wave" variant="rectangular" height={48} />
								<Skeleton animation="wave" variant="rectangular" height={48} />
							</Stack>
						)}
					</Card.CardContent>
				</Card>
				<Alert severity="info" variant="filled" className="mb-2">
					<Typography className="font-weight-bold mb-1">注意事項</Typography>
					<Box component="ul" className="list-default mb-0">
						<li>現場首購循環訂單商品所累積的點數將會保留，並在首次出貨完成時統一補入會員帳戶中。</li>
						<li>馬拉松累點活動與抵用券優惠不得併用。使用抵用券請切換至「新增收入」結帳。</li>
                        <li>首盒補點僅限購買同品類商品 (e.g.循訂乳清首盒購買僅限乳清；循訂乳清但首盒購買鈣力嗨不可補點)。</li>
                        <li>首盒購買盒數不可大於循環訂單。</li>
					</Box>
				</Alert>
				<Grid container spacing={2}>
					{/* 訂單發票與其他 */}
					<Grid item xs={6}>
						<Card>
							<Card.CardContent className="p-4">
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<OrderInvoiceSection
											ref={invoiceSectionRef}
											sourceData={{
												issuingType: invoiceSetting?.issuingType,
												invoiceCarrierType: invoiceSetting?.invoiceCarrierType,
												invoiceCarrierNo: invoiceSetting?.invoiceCarrierNo,
												donationCode: invoiceSetting?.donationCode,
												invoiceNo: invoiceSetting?.invoiceNo,
												taxID: invoiceSetting?.taxID,
												taxTitle: invoiceSetting?.taxTitle,
												issuingEmail: member?.issuingEmail || member?.mail,
											}}
											options={{
												invoiceTypeOptions: options?.invoiceTypeOptions ?? [],
												invoiceCarrierTypeOptions: options?.invoiceCarrierTypeOptions ?? [],
											}}
											errorModule={validationErrors}
										/>
									</Grid>
								</Grid>
							</Card.CardContent>
						</Card>
					</Grid>

					{/* 價格摘要與擔當員工 */}
					<Grid item xs={6}>
						<Card className="mb-2">
							<Card.CardContent className="p-4">
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<Box className="mb-2 price-summary-section">
											{/* 總收款金額 */}
											<Stack className="mb-2" justifyContent="space-between" alignItems="center">
												<Box className="font-weight-bold">總收款金額</Box>
												<Typography variant="h6" className="mb-0 font-weight-bold">
													{formatCurrencyFn(orderTotal, { symbol: "$" })}
												</Typography>
											</Stack>

											{/* 實收金額 */}
											<Stack className="mb-2" justifyContent="space-between" alignItems="center">
												<Box>實收金額</Box>
												<Typography variant="body1" className="mb-0">
													{formatCurrencyFn(paidAmount, { symbol: "$" })}
												</Typography>
											</Stack>

											{/* 狀態提示 */}
											{balanceStatus && (
												<Alert severity={balanceStatus.severity} color={balanceStatus.color} variant="filled" className="mb-4">
													<Typography variant="body1" className="mb-0 font-weight-bold">
														{balanceStatus.message}
													</Typography>
												</Alert>
											)}

											{/* 現金收款金額 */}
											<Stack className="my-4" justifyContent="space-between" alignItems="center">
												<Box>現金收款金額</Box>
												<Box className="price-summary-input-wrap">
													<TextField
														ref={cashPaymentRef}
														key={`cash-${orderTotal}`}
														defaultValue={cashPayment}
														maskType={"MONEY"}
														inputProps={{ placeholder: "請輸入金額", min: 0 }}
														onChangeNative={onCashPaymentChange}
														fullWidth
														required
													/>
												</Box>
											</Stack>

											{/* 信用卡收款金額 */}
											<Stack className="price-summary-item" justifyContent="space-between" alignItems="center">
												<Box>信用卡收款金額</Box>
												<Box className="price-summary-input-wrap">
													<TextField
														ref={cardPaymentRef}
														key={`card-${orderTotal}`}
														defaultValue={cardPayment}
														maskType={"MONEY"}
														inputProps={{ placeholder: "請輸入金額", min: 0 }}
														onChangeNative={onCardPaymentChange}
														fullWidth
														required
													/>
												</Box>
											</Stack>
										</Box>
									</Grid>
									<Grid item xs={12}>
										<Divider className="mb-2" />
									</Grid>
									<Grid item xs={6}>
										<EmployeeSelect label="擔當" ref={employeeRef} options={options?.coachOptions} {...getErrorModuleStatus(validationErrors, "employeeID")} fullWidth required />
									</Grid>
									<Grid xs={6} item>
										<DateField label="建立日期" ref={orderDateRef} defaultValue={getNowTime()} minDate={getFirstOfMonth(getNowTime())} maxDate={getNowTime()} fullWidth />
									</Grid>
								</Grid>
							</Card.CardContent>
						</Card>

						<Stack justifyContent="end" alignItems="center" spacing={2}>
							<Button variant="contained" type="submit">
								送出結帳
							</Button>
						</Stack>
					</Grid>
				</Grid>
			</DialogContent>
		</Dialog>
	);
});

export default HealthyCycleFirstOrderDialog;
